/**
 * Speisekarten-Verwaltung (P2, docs/KONZEPT-SPEISEKARTEN.md) — unter dem Katalog erreichbar
 * (kein eigener Nav-Eintrag, Sidebar ≤ 9). Karten-Grid mit Art, Zählern, Gültigkeit,
 * aktiv-Toggle; Aktionen: bearbeiten (Name/Art/Gültigkeit), ersetzen (→ Import-Wizard mit
 * vorgewählter Karte), duplizieren (Saison-Workflow), Kategorien zuordnen (Bestand),
 * archivieren (Soft-Delete mit Kaskade + Bestätigung mit Zählern).
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApi, apiMutate } from "../hooks/useApi";
import type { CatalogCategory, MenuCard } from "../ui/types";
import { Drawer, EmptyState, ErrorNote, Icon, LoadingRows, PageHeader } from "../ui/components";

const KIND_LABEL: Record<MenuCard["kind"], string> = { food: "Speisekarte", drinks: "Getränkekarte", mixed: "Gemischt" };
const KIND_ICON: Record<MenuCard["kind"], string> = { food: "restaurant", drinks: "local_bar", mixed: "restaurant_menu" };

function fmtDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString("de-DE");
}
/** Ablauf-Badge: abgelaufen (rot) / läuft in ≤ 14 Tagen ab (gelb). */
function expiryBadge(card: MenuCard): { label: string; tone: "red" | "amber" } | null {
  if (!card.validUntil) return null;
  const until = new Date(card.validUntil);
  if (Number.isNaN(until.getTime())) return null;
  const today = new Date(new Date().toISOString().slice(0, 10));
  if (until < today) return { label: "abgelaufen", tone: "red" };
  const days = Math.round((until.getTime() - today.getTime()) / 86_400_000);
  if (days <= 14) return { label: `läuft in ${days} Tag${days === 1 ? "" : "en"} ab`, tone: "amber" };
  return null;
}

export default function KartenPage() {
  const navigate = useNavigate();
  const { data, loading, error, reload } = useApi<MenuCard[]>("/api/menu-cards");
  const catsQ = useApi<CatalogCategory[]>("/api/catalog/categories");
  const [editing, setEditing] = useState<MenuCard | null>(null);
  const [assignFor, setAssignFor] = useState<MenuCard | null>(null);
  const [assignSel, setAssignSel] = useState<Set<string>>(new Set());
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Mehrsprachigkeit Weg B: KI-Uebersetzung einer Karte (Sprachwahl im Drawer).
  const [translateFor, setTranslateFor] = useState<MenuCard | null>(null);
  const [translateLang, setTranslateLang] = useState("en");
  const [translateResult, setTranslateResult] = useState<string | null>(null);

  const cards = data ?? [];
  // Bestands-Zuordnung: nur aktive Kategorien OHNE Karte sind zuordenbar.
  const unassignedCats = (catsQ.data ?? []).filter((c) => c.archivedAt == null && (c.cardId ?? null) == null);

  async function patchCard(card: MenuCard, patch: Partial<MenuCard>) {
    try {
      await apiMutate(`/api/menu-cards/${card.id}`, "PATCH", patch);
      setFormError(null);
      reload();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : String(e));
    }
  }

  async function archiveCard(card: MenuCard) {
    const ok = confirm(
      `Karte „${card.name}" archivieren?\n\nDamit werden auch ihre ${card.categoryCount ?? 0} Kategorien und ${card.itemCount ?? 0} Positionen archiviert (nicht gelöscht – Angebote und Pakete behalten ihre Referenzen).`,
    );
    if (!ok) return;
    try {
      await apiMutate(`/api/menu-cards/${card.id}`, "DELETE");
      reload();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : String(e));
    }
  }

  async function duplicateCard(card: MenuCard) {
    setBusy(true);
    try {
      await apiMutate(`/api/menu-cards/${card.id}/duplicate`, "POST", {});
      setFormError(null);
      reload();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function runTranslate() {
    if (!translateFor) return;
    setBusy(true);
    setTranslateResult(null);
    try {
      const res = await apiMutate<{ lang: string; translatedItems: number; translatedCategories: number; totalItems: number }>(
        `/api/menu-cards/${translateFor.id}/translate`,
        "POST",
        { lang: translateLang },
      );
      setTranslateResult(`${res.translatedItems} von ${res.totalItems} Positionen und ${res.translatedCategories} Kategorien übersetzt (${res.lang.toUpperCase()}). Feinschliff: Position im Katalog öffnen.`);
      setFormError(null);
      reload();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function assignCategories() {
    if (!assignFor || assignSel.size === 0) return;
    setBusy(true);
    try {
      await apiMutate(`/api/menu-cards/${assignFor.id}/assign-categories`, "POST", { categoryIds: [...assignSel] });
      setAssignFor(null);
      setAssignSel(new Set());
      setFormError(null);
      reload();
      void catsQ.reload();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Speisekarten"
        subtitle="Karten gruppieren den Katalog und lassen sich einzeln erneuern – die Mittagskarte bleibt unberührt, wenn die Saisonkarte wechselt."
        actions={
          <>
            <button type="button" className="btn-secondary" onClick={() => navigate("/katalog")}>
              <Icon name="arrow_back" className="text-[18px]" /> Zum Katalog
            </button>
            <button type="button" className="btn-primary" onClick={() => navigate("/katalog/import")}>
              <Icon name="upload_file" className="text-[18px]" /> Karte importieren
            </button>
          </>
        }
      />

      {formError && <ErrorNote message={formError} />}

      {loading ? (
        <LoadingRows />
      ) : error ? (
        <ErrorNote message={error} />
      ) : cards.length === 0 ? (
        <EmptyState
          icon="menu_book"
          title="Noch keine Karten"
          hint="Importiere eine Speisekarte und wähle im Review »Neue Karte anlegen« – oder ordne bestehende Kategorien einer Karte zu."
          cta={{ label: "Speisekarte importieren", icon: "upload_file", onClick: () => navigate("/katalog/import") }}
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map((card) => {
            const badge = expiryBadge(card);
            const validity = [fmtDate(card.validFrom), fmtDate(card.validUntil)];
            return (
              <div key={card.id} className="card flex flex-col gap-3 p-5">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex min-w-0 items-start gap-2.5">
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[9px] bg-primary/12 text-primary">
                      <Icon name={KIND_ICON[card.kind]} className="text-[18px]" />
                    </span>
                    <div className="min-w-0">
                      <p className="truncate font-body-lg text-body-lg font-semibold text-on-background dark:text-inverse-on-surface">{card.name}</p>
                      <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                        {KIND_LABEL[card.kind]} · {card.categoryCount ?? 0} Kategorien · {card.itemCount ?? 0} Positionen
                      </p>
                    </div>
                  </div>
                  {!card.active && <span className="shrink-0 rounded-full bg-surface-container-high px-2 py-0.5 text-label-md dark:bg-surface-variant/40">inaktiv</span>}
                </div>

                {/* Mehrsprachigkeit: Sprach-Status (uebersetzte Positionen je Sprache). */}
                {Object.keys(card.translationCoverage ?? {}).length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5">
                    {Object.entries(card.translationCoverage ?? {}).map(([lang, n]) => (
                      <span
                        key={lang}
                        className={`rounded px-1.5 py-0.5 font-label-md text-[11px] font-semibold ${
                          n >= (card.itemCount ?? 0)
                            ? "bg-green-500/15 text-green-700 dark:text-green-300"
                            : "bg-amber-500/15 text-amber-700 dark:text-amber-300"
                        }`}
                        title={`${n} von ${card.itemCount ?? 0} Positionen auf ${lang.toUpperCase()} übersetzt`}
                      >
                        {lang.toUpperCase()} {n}/{card.itemCount ?? 0}
                      </span>
                    ))}
                  </div>
                )}

                {(validity[0] || validity[1] || badge) && (
                  <div className="flex flex-wrap items-center gap-2 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                    {(validity[0] || validity[1]) && (
                      <span className="inline-flex items-center gap-1">
                        <Icon name="event" className="text-[15px]" />
                        {validity[0] ?? "…"} – {validity[1] ?? "…"}
                      </span>
                    )}
                    {badge && (
                      <span
                        className={`rounded px-1.5 py-0.5 font-label-md text-[11px] font-semibold ${
                          badge.tone === "red" ? "bg-error/10 text-error" : "bg-amber-500/15 text-amber-700 dark:text-amber-300"
                        }`}
                      >
                        {badge.label}
                      </span>
                    )}
                  </div>
                )}

                <div className="mt-auto flex flex-wrap items-center gap-2 border-t border-outline-variant/30 pt-3 dark:border-outline/30">
                  <button type="button" className="btn-secondary flex-1 justify-center" onClick={() => setEditing(card)}>
                    <Icon name="edit" className="text-[16px]" /> Bearbeiten
                  </button>
                  <button
                    type="button"
                    className="btn-secondary flex-1 justify-center"
                    title="Neue Version dieser Karte importieren – Weggefallenes wird archiviert"
                    onClick={() => navigate(`/katalog/import?cardId=${card.id}&mode=replace`)}
                  >
                    <Icon name="sync_alt" className="text-[16px]" /> Ersetzen
                  </button>
                  <button
                    type="button"
                    className="rounded-lg p-2 text-secondary hover:bg-surface-container-high dark:hover:bg-surface-variant/30"
                    title={card.active ? "Deaktivieren (nicht mehr im KI-Vorschlag/Picker)" : "Aktivieren"}
                    onClick={() => void patchCard(card, { active: !card.active })}
                  >
                    <Icon name={card.active ? "toggle_on" : "toggle_off"} />
                  </button>
                  <button
                    type="button"
                    className="rounded-lg p-2 text-secondary hover:bg-surface-container-high dark:hover:bg-surface-variant/30"
                    title="Karte duplizieren (Saison-Workflow)"
                    disabled={busy}
                    onClick={() => void duplicateCard(card)}
                  >
                    <Icon name="content_copy" className="text-[18px]" />
                  </button>
                  <button
                    type="button"
                    className="rounded-lg p-2 text-secondary hover:bg-surface-container-high dark:hover:bg-surface-variant/30"
                    title="KI-Übersetzung der Karte (Namen + Beschreibungen; Preise/Allergene bleiben unberührt)"
                    onClick={() => {
                      setTranslateFor(card);
                      setTranslateResult(null);
                    }}
                  >
                    <Icon name="translate" className="text-[18px]" />
                  </button>
                  {unassignedCats.length > 0 && (
                    <button
                      type="button"
                      className="rounded-lg p-2 text-secondary hover:bg-surface-container-high dark:hover:bg-surface-variant/30"
                      title="Bestehende Kategorien (Ohne Karte) dieser Karte zuordnen"
                      onClick={() => {
                        setAssignFor(card);
                        setAssignSel(new Set());
                      }}
                    >
                      <Icon name="playlist_add" className="text-[18px]" />
                    </button>
                  )}
                  <button
                    type="button"
                    className="rounded-lg p-2 text-error hover:bg-error/10"
                    title="Archivieren (Soft-Delete inkl. Kategorien + Positionen)"
                    onClick={() => void archiveCard(card)}
                  >
                    <Icon name="archive" className="text-[18px]" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Bearbeiten: Name, Art, Gültigkeit */}
      <Drawer open={editing != null} onClose={() => setEditing(null)} title={editing ? `Karte – ${editing.name}` : ""}>
        {editing && (
          <div className="space-y-4">
            {formError && <ErrorNote message={formError} />}
            <label className="block">
              <span className="mb-1 block font-body-sm text-body-sm font-semibold">Name</span>
              <input
                className="input"
                defaultValue={editing.name}
                onBlur={(e) => e.target.value.trim() && e.target.value.trim() !== editing.name && void patchCard(editing, { name: e.target.value.trim() })}
              />
            </label>
            <label className="block">
              <span className="mb-1 block font-body-sm text-body-sm font-semibold">Art</span>
              <select
                className="input"
                defaultValue={editing.kind}
                onChange={(e) => void patchCard(editing, { kind: e.target.value as MenuCard["kind"] })}
              >
                <option value="food">Speisekarte</option>
                <option value="drinks">Getränkekarte</option>
                <option value="mixed">Gemischt (Speisen + Getränke)</option>
              </select>
              <span className="mt-1 block font-body-sm text-[12px] text-on-surface-variant/70">
                Die Art bestimmt Art und MwSt. NEU importierter Positionen; bestehende bleiben unverändert.
              </span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="mb-1 block font-body-sm text-body-sm font-semibold">Gültig von</span>
                <input
                  type="date"
                  className="input"
                  defaultValue={editing.validFrom ?? ""}
                  onChange={(e) => void patchCard(editing, { validFrom: e.target.value || null })}
                />
              </label>
              <label className="block">
                <span className="mb-1 block font-body-sm text-body-sm font-semibold">Gültig bis</span>
                <input
                  type="date"
                  className="input"
                  defaultValue={editing.validUntil ?? ""}
                  onChange={(e) => void patchCard(editing, { validUntil: e.target.value || null })}
                />
              </label>
            </div>
            <p className="font-body-sm text-[12px] text-on-surface-variant/70">
              Außerhalb des Fensters (bezogen aufs Veranstaltungsdatum) schlägt die KI keine Positionen dieser Karte mehr vor.
            </p>
            <div className="flex justify-end">
              <button type="button" className="btn-primary" onClick={() => setEditing(null)}>Fertig</button>
            </div>
          </div>
        )}
      </Drawer>

      {/* Mehrsprachigkeit Weg B: KI-Übersetzung der Karte */}
      <Drawer open={translateFor != null} onClose={() => setTranslateFor(null)} title={translateFor ? `KI-Übersetzung – ${translateFor.name}` : ""}>
        {translateFor && (
          <div className="space-y-4">
            {formError && <ErrorNote message={formError} />}
            <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
              Übersetzt Namen und Beschreibungen aller {translateFor.itemCount ?? 0} Positionen sowie die Kategorie-Titel.
              Preise, Allergene und Struktur bleiben unangetastet. Etablierte Gerichtenamen (z. B. „Vitello Tonnato") werden nicht übersetzt.
              Bestehende Übersetzungen der gewählten Sprache werden überschrieben.
            </p>
            <label className="block">
              <span className="mb-1 block font-body-sm text-body-sm font-semibold">Zielsprache</span>
              <select className="input" value={translateLang} onChange={(e) => setTranslateLang(e.target.value)}>
                <option value="en">Englisch</option>
                <option value="it">Italienisch</option>
                <option value="fr">Französisch</option>
                <option value="es">Spanisch</option>
              </select>
            </label>
            {translateResult && (
              <p className="rounded-lg bg-green-500/10 px-3 py-2 font-body-sm text-body-sm text-green-800 dark:text-green-300">{translateResult}</p>
            )}
            <div className="flex justify-end gap-3">
              <button type="button" className="btn-secondary" onClick={() => setTranslateFor(null)}>Schließen</button>
              <button type="button" className="btn-primary" disabled={busy} onClick={() => void runTranslate()}>
                {busy ? "Übersetzt …" : "Jetzt übersetzen"}
              </button>
            </div>
          </div>
        )}
      </Drawer>

      {/* Bestands-Zuordnung: Ohne-Karte-Kategorien → diese Karte */}
      <Drawer open={assignFor != null} onClose={() => setAssignFor(null)} title={assignFor ? `Kategorien zuordnen – ${assignFor.name}` : ""}>
        {assignFor && (
          <div className="space-y-4">
            <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
              Wähle Kategorien ohne Karten-Zuordnung, die zu dieser Karte gehören sollen (inklusive ihrer Positionen).
            </p>
            {unassignedCats.length === 0 ? (
              <p className="rounded-lg border border-dashed border-outline-variant/50 p-4 text-center font-body-sm text-body-sm text-secondary">
                Keine Kategorien ohne Karte vorhanden.
              </p>
            ) : (
              <div className="space-y-1.5">
                {unassignedCats.map((c) => (
                  <label key={c.id} className="flex cursor-pointer items-center gap-2 rounded-lg border border-outline-variant/30 px-3 py-2 font-body-sm text-body-sm dark:border-outline/30">
                    <input
                      type="checkbox"
                      checked={assignSel.has(c.id)}
                      onChange={(e) => {
                        const next = new Set(assignSel);
                        if (e.target.checked) next.add(c.id);
                        else next.delete(c.id);
                        setAssignSel(next);
                      }}
                    />
                    {c.name}
                  </label>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button type="button" className="btn-secondary" onClick={() => setAssignFor(null)}>Abbrechen</button>
              <button type="button" className="btn-primary" disabled={busy || assignSel.size === 0} onClick={() => void assignCategories()}>
                {assignSel.size} zuordnen
              </button>
            </div>
          </div>
        )}
      </Drawer>
    </>
  );
}
